// eslint-disable-next-line import/named
import { customElement } from 'lit/decorators.js';
import { DataBinding } from '../../../webmodule-common/other/ui/databinding/databinding';
import { DataProvider, ProviderBackup } from '../../../webmodule-common/other/data-provider';
import {
  DataTracker,
  DynamicValueBinder,
  FieldType
} from '../../../webmodule-common/other/ui/databinding/data-tracker';
import { EventTemplate, Snippet } from '../../../webmodule-common/other/ui/events';
import { fireQuickSuccessToast } from '../../../webmodule-common/other/toast-away';
import { FormInputAssistant } from '../../../webmodule-common/other/ui/templateresult/form-input-assistant';
import { html } from 'lit';
import { isAutoSaving } from '../../../webmodule-common/other/save-workflow';
import { isSupplierUnitsMetric } from '../../quotes/data/v6/helper-functions';
import { LangFranchiseeFabricatorSettingsView } from '../../../localization/scripts/franchisee/view/franchisee-fabricator-settings-view-lang';
import { PageControlTabWithIndependantSaving } from '../../../webmodule-common/other/ui/data-entry-screen-base';
import {
  v6Config,
  v6SupportsVersion,
  v6Util,
  v6VersionMap
} from '../../../webmodule-common/interop/interfaces/v6-config';

// Each kerf field stores both value and display because parseMeasurement returns
// both and neither can be reliably reconstructed from the other: *Value fields hold the
// internal numeric string used by downstream logic, *Display fields hold the formatted
// text (units, fractions) bound to the UI so reloaded records show the user's original input.
export interface FabricatorSettings {
  kerfValue: string;
  kerfDisplay: string;
  kerfAngleValue: string;
  kerfAngleDisplay: string;
}

@customElement('wm-franchiseefabricatorsettingsview')
export class FranchiseeFabricatorSettingsView extends PageControlTabWithIndependantSaving {
  private backup: ProviderBackup<FabricatorSettings>;

  private dataBinding: DataBinding;
  private dataTracker: DataTracker;

  private supplierId: string = '';
  private isMetric = true;
  private validationErrors: string[] = [];

  constructor(dataProvider: DataProvider<FabricatorSettings>) {
    super();
    this.pageFragment = 'fabricator-settings';
    this.backup = new ProviderBackup<FabricatorSettings>(dataProvider);

    this.dataBinding = new DataBinding(this.ui, this.elementId, input => {
      return `${input}-${this.elementId}`;
    });
    this.dataTracker = new DataTracker(this.dataBinding);

    this.dataTracker.addBinding(
      new DynamicValueBinder(
        FieldType.string,
        false,
        () => this.backup.item.kerfDisplay,
        value => {
          this.backup.item.kerfDisplay = (value as string) ?? '';
        },
        () => false
      ),
      'kerf',
      'kerf',
      FieldType.string,
      false
    );

    this.dataTracker.addBinding(
      new DynamicValueBinder(
        FieldType.string,
        false,
        () => this.backup.item.kerfAngleDisplay,
        value => {
          this.backup.item.kerfAngleDisplay = (value as string) ?? '';
        },
        () => false
      ),
      'kerfAngle',
      'kerfAngle',
      FieldType.string,
      false
    );

    this.dataTracker.eventChange = () => this.requestUpdate();
  }

  public isDataReadonly(): boolean {
    return false;
  }

  public internalDataChanged(): boolean {
    return this.backup.changed;
  }

  public allowDeletePage(): boolean {
    return false;
  }

  async onEnter(): Promise<void> {
    this.backup.reset();
    this.validationErrors = [];

    // Resolve supplier for V6 calls. Errors here are not surfaced to the user
    // because this is page-load initialisation - if the supplier is unavailable,
    // prepareForSave() will report a validation error when the user actually saves.
    try {
      const suppliers = await v6Config().suppliers();
      const onlineSupplier = suppliers?.find(s => s.online);
      this.supplierId = onlineSupplier?.id ?? '';
      if (this.supplierId) {
        try {
          this.isMetric = isSupplierUnitsMetric(this.supplierId);
        } catch {
          // v6Config may not be loaded yet - safe to default to metric (field default)
        }
      }
    } catch {
      // supplierId stays empty; prepareForSave() checks this and shows supplierOffline error
      this.supplierId = '';
    }

    if (!this.backup.item.kerfDisplay) {
      this.backup.item.kerfDisplay = this.isMetric ? '0 mm' : '0"';
      this.backup.item.kerfValue = '0';
    }

    if (!this.backup.item.kerfAngleDisplay) {
      this.backup.item.kerfAngleDisplay = this.isMetric ? '0 mm' : '0"';
      this.backup.item.kerfAngleValue = '0';
    }

    this.requestUpdate();
  }

  protected async revertChanges(): Promise<boolean> {
    super.revertChanges();
    this.backup.reset();
    this.validationErrors = [];
    return true;
  }

  public async prepareForSave(): Promise<void> {
    // Flush any pending input values from the UI into the data model
    this.dataTracker.applyChangeToValue();

    // Validate and parse both kerf inputs via V6 before save
    this.validationErrors = [];

    if (!this.supplierId) {
      this.validationErrors.push(LangFranchiseeFabricatorSettingsView.supplierOffline());
      return;
    }

    if (!v6SupportsVersion(v6VersionMap.hasMeasurementParsing)) {
      this.validationErrors.push(LangFranchiseeFabricatorSettingsView.measurementParsingUnsupported());
      return;
    }

    // Validate straight kerf
    const kerfText = this.backup.item.kerfDisplay.trim();
    if (!kerfText) {
      this.backup.item.kerfValue = '0';
      this.backup.item.kerfDisplay = this.isMetric ? '0 mm' : '0"';
    } else {
      try {
        const result = await v6Util().parseMeasurement(this.supplierId, kerfText);
        if (result?.valid) {
          this.backup.item.kerfValue = result.internalValue.toString();
          this.backup.item.kerfDisplay = result.displayText;
        } else {
          this.validationErrors.push(LangFranchiseeFabricatorSettingsView.invalidMeasurementFormat());
        }
      } catch {
        this.validationErrors.push(LangFranchiseeFabricatorSettingsView.invalidMeasurementFormat());
      }
    }

    // Validate angled kerf
    const kerfAngleText = this.backup.item.kerfAngleDisplay.trim();
    if (!kerfAngleText) {
      this.backup.item.kerfAngleValue = '0';
      this.backup.item.kerfAngleDisplay = this.isMetric ? '0 mm' : '0"';
    } else {
      try {
        const result = await v6Util().parseMeasurement(this.supplierId, kerfAngleText);
        if (result?.valid) {
          this.backup.item.kerfAngleValue = result.internalValue.toString();
          this.backup.item.kerfAngleDisplay = result.displayText;
        } else {
          this.validationErrors.push(LangFranchiseeFabricatorSettingsView.invalidMeasurementFormat());
        }
      } catch {
        this.validationErrors.push(LangFranchiseeFabricatorSettingsView.invalidMeasurementFormat());
      }
    }
  }

  getValidationErrors(): string[] {
    return this.validationErrors;
  }

  protected async internalSaveData(): Promise<boolean> {
    const result = await this.backup.commitChanges();
    if (result) {
      if (!isAutoSaving()) fireQuickSuccessToast(LangFranchiseeFabricatorSettingsView.fabricatorSettingsSaved());
      // Why requestUpdate here? prepareForSave overwrote kerfDisplay/kerfAngleDisplay with
      // V6-formatted text, but those mutations bypass dataTracker.eventChange so the inputs
      // would keep showing the user's literal entry until something else triggered a render.
      this.requestUpdate();
      return true;
    }
    return false;
  }

  protected getCaption(): Snippet {
    return LangFranchiseeFabricatorSettingsView.fabricatorSettings();
  }

  protected bodyTemplate(): EventTemplate {
    const forms = new FormInputAssistant(this.dataTracker, this.isDataReadonly());
    forms.immediateBindingUpdate = true;

    return html`
      <div>
        <form class="form-two-col">
          <h2>${LangFranchiseeFabricatorSettingsView.optimizerSettings()}</h2>
          <div class="row">
            <div>${forms.text1('kerf', LangFranchiseeFabricatorSettingsView.kerf(), {})}</div>
          </div>
          <div class="row">
            <div>${forms.text1('kerfAngle', LangFranchiseeFabricatorSettingsView.kerfAngle(), {})}</div>
          </div>
        </form>
      </div>
    `;
  }
}
